// A tab opened before a release keeps running its old bundle. Its response
// schemas then reject the new server (26.09.2026: a strict delivery item met
// the new `retryAfterContactFix` field) and a data retry cannot help; only the
// new bundle can. Vite names the entry script by build hash, so comparing the
// loaded entry with the one index.html serves now detects the skew. The page's
// async first script (boot.ts) is not the entry: it hardly changes between
// releases.

const RELOAD_MARK_KEY = "vekha:build-reload";
const RELOAD_GUARD_MS = 60_000;

export function moduleEntryScript(html: string): string | null {
  for (const match of html.matchAll(/<script\b([^>]*)>/giu)) {
    const attributes = match[1] ?? "";
    if (!/\btype\s*=\s*["']module["']/iu.test(attributes) || /\basync\b/iu.test(attributes)) continue;
    const source = /\bsrc\s*=\s*["']([^"']+)["']/iu.exec(attributes)?.[1];
    if (source) return source;
  }
  return null;
}

export type BuildFreshnessEnvironment = Readonly<{
  loadedEntry: () => string | null;
  fetchServedHtml: () => Promise<string | null>;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  reload: () => void;
  now: () => number;
}>;

/** Reloads the page when the server now serves another build. */
export async function reloadIfNewBuild(
  environment: BuildFreshnessEnvironment = browserEnvironment(),
): Promise<boolean> {
  const loaded = environment.loadedEntry();
  if (!loaded) return false;
  let served: string | null;
  try {
    const html = await environment.fetchServedHtml();
    served = html === null ? null : moduleEntryScript(html);
  } catch {
    return false;
  }
  if (!served || served === loaded) return false;
  // One reload per served build and minute: a release that serves a broken
  // index.html must not turn the unavailable screen into a reload loop.
  try {
    const [previousBuild, previousAt] = environment.storage?.getItem(RELOAD_MARK_KEY)?.split(" ") ?? [];
    if (previousBuild === served && environment.now() - Number(previousAt) < RELOAD_GUARD_MS) return false;
    environment.storage?.setItem(RELOAD_MARK_KEY, `${served} ${environment.now()}`);
  } catch {
    // Blocked storage: after the reload the loaded entry equals the served one.
  }
  environment.reload();
  return true;
}

function browserEnvironment(): BuildFreshnessEnvironment {
  return {
    loadedEntry: () => document
      .querySelector<HTMLScriptElement>('script[type="module"][src]:not([async])')
      ?.getAttribute("src") ?? null,
    fetchServedHtml: async () => {
      const response = await fetch("/", {
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      });
      return response.ok ? await response.text() : null;
    },
    storage: sessionStorageOrNull(),
    reload: () => window.location.reload(),
    now: Date.now,
  };
}

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

// A tab whose build was replaced asks for a chunk that the server no longer has
// (26.10.2026: «Failed to fetch dynamically imported module …/webinars-page-….js»).
// Each browser words it its own way.
const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|is not a valid JavaScript MIME type/iu;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === "ChunkLoadError" || CHUNK_LOAD_ERROR.test(error.message);
}

const CHUNK_RELOAD_KEY = "vekha:chunk-reload";

export type ChunkReloadEnvironment = Readonly<{
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  reload: () => void;
  now: () => number;
}>;

/** Reloads the page once a minute at most: a chunk missing from the new build too must not loop. */
export function reloadOnceAfterChunkError(
  environment: ChunkReloadEnvironment = browserEnvironment(),
): boolean {
  try {
    const previousAt = Number(environment.storage?.getItem(CHUNK_RELOAD_KEY));
    if (!environment.storage || environment.now() - previousAt < RELOAD_GUARD_MS) return false;
    environment.storage.setItem(CHUNK_RELOAD_KEY, String(environment.now()));
  } catch {
    // Without storage a reload could not tell itself from the previous one.
    return false;
  }
  environment.reload();
  return true;
}

// A tab left open across a release keeps showing the old build's screens; the owner reads their numbers as the
// right ones (Ден 26.10.2026). The API names the build it serves (`/api/version`); a tab on another build moves
// to it at the next navigation, on return to the tab or on the timer — not in the middle of an edit.

export type BuildWatchEnvironment = Readonly<{
  loadedEntry: () => string | null;
  fetchServedBuild: () => Promise<string | null>;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  reload: () => void;
  now: () => number;
}>;

/** The build the server serves now when it is not the tab's own, else null. */
export async function newerServedBuild(environment: BuildWatchEnvironment = buildWatchEnvironment()): Promise<string | null> {
  const loaded = environment.loadedEntry();
  if (!loaded) return null;
  try {
    const served = await environment.fetchServedBuild();
    return served && served !== loaded ? served : null;
  } catch {
    return null;
  }
}

/** Reloads into the served build, once a minute at most for the same build: a cache serving the old page must not loop. */
export function reloadIntoBuild(served: string, environment: BuildWatchEnvironment = buildWatchEnvironment()): boolean {
  try {
    const [previousBuild, previousAt] = environment.storage?.getItem(RELOAD_MARK_KEY)?.split(" ") ?? [];
    if (previousBuild === served && environment.now() - Number(previousAt) < RELOAD_GUARD_MS) return false;
    environment.storage?.setItem(RELOAD_MARK_KEY, `${served} ${environment.now()}`);
  } catch {
    // Blocked storage: after the reload the loaded entry equals the served one.
  }
  environment.reload();
  return true;
}

export function buildWatchEnvironment(): BuildWatchEnvironment {
  const browser = browserEnvironment();
  return {
    loadedEntry: browser.loadedEntry,
    fetchServedBuild: async () => {
      const response = await fetch("/api/version", { cache: "no-store", credentials: "same-origin" });
      if (!response.ok) return null;
      const body = await response.json() as { build?: unknown };
      return typeof body.build === "string" ? body.build : null;
    },
    storage: browser.storage,
    reload: browser.reload,
    now: browser.now,
  };
}

const CHECK_SPACING_MS = 30_000;

/** The decisions of a tab on an old build; the page only calls them on navigation, on return to the tab and on the timer. */
export function createBuildWatch(
  environment: BuildWatchEnvironment,
  busy: () => boolean,
  onWaiting: () => void,
): Readonly<{ check: () => Promise<void> }> {
  let served: string | null = null;
  let askedAt = Number.NEGATIVE_INFINITY;
  const settle = () => {
    if (!served) return;
    if (busy() || !reloadIntoBuild(served, environment)) onWaiting();
  };
  return {
    check: async () => {
      // Navigation asks too: at most every 30 s, the answer is the same between releases.
      if (!served && environment.now() - askedAt >= CHECK_SPACING_MS) {
        askedAt = environment.now();
        served = await newerServedBuild(environment);
      }
      settle();
    },
  };
}

const TEXT_INPUT = /^(?:text|search|email|tel|url|password|number|date|datetime-local|month|time|week)$/u;

type EditableElement = Readonly<{ tagName: string; type?: string; isContentEditable?: boolean }>;
export type EditDocument = Readonly<{ activeElement: EditableElement | null; querySelector: (selector: string) => unknown }>;

/** Typing, an open dialog or a form with unsaved changes: a reload now would lose what the person is doing. */
export function editInProgress(document: EditDocument, unsavedChanges: boolean): boolean {
  if (unsavedChanges) return true;
  const active = document.activeElement;
  const tag = active?.tagName.toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT" || active?.isContentEditable) return true;
  if (tag === "INPUT" && TEXT_INPUT.test(active?.type || "text")) return true;
  return document.querySelector('dialog[open], [role="dialog"], [role="alertdialog"]') !== null;
}
